import {
   getInstructorDisplayName,
   normalizeSectorSlug,
} from "./extremeInstructorMeta";
import { resolveInstructorHeaderStyles } from "./extremeEventColors";

const EMPTY_LIST = [];
const pad2 = (n) => String(n).padStart(2, "0");

export function dateInputValueFromAny(value) {
   if (!value) return "";
   if (typeof value === "string") {
      const direct = value.match(/^(\d{4}-\d{2}-\d{2})/);
      if (direct?.[1]) return direct[1];
   }
   const parsed = new Date(value);
   if (Number.isNaN(parsed.getTime())) return "";
   return `${parsed.getFullYear()}-${pad2(parsed.getMonth() + 1)}-${pad2(
      parsed.getDate(),
   )}`;
}

export function getDefaultSubstitutionDate(monthValue, fallbackMonthKey = "") {
   const monthKeyValue = String(monthValue || fallbackMonthKey).trim();
   const today = dateInputValueFromAny(new Date());
   if (monthKeyValue && today.startsWith(`${monthKeyValue}-`)) return today;
   if (/^\d{4}-\d{2}$/.test(monthKeyValue)) return `${monthKeyValue}-01`;
   return today;
}

export function getMonthSubstitutionDateRange(monthValue) {
   const match = String(monthValue || "").match(/^(\d{4})-(\d{2})$/);
   if (!match) return null;
   const year = Number(match[1]);
   const month = Number(match[2]);
   if (!Number.isFinite(year) || !Number.isFinite(month) || month < 1 || month > 12) {
      return null;
   }
   const start = new Date(year, month - 1, 1);
   const end = new Date(year, month, 0);
   return {
      dateFrom: dateInputValueFromAny(start),
      dateTo: dateInputValueFromAny(end),
   };
}

function getSubstitutionRowBaseId(row) {
   return String(
      row?.baseInstructorId ??
         row?.base_instructor_id ??
         row?.baseInstructor?.id ??
         row?.base_instructor?.id ??
         "",
   ).trim();
}

export function resolveSubstitutionOverlayEntry(row, substituteInstructorById) {
   const substituteId = String(
      row?.substituteInstructorId ??
         row?.substitute_instructor_id ??
         row?.substituteInstructor?.id ??
         row?.substitute_instructor?.id ??
         "",
   ).trim();
   if (!substituteId) return null;

   const fromApi = row?.substituteInstructor ?? row?.substitute_instructor ?? null;
   const cached = substituteInstructorById?.get?.(substituteId) || null;
   const name = fromApi
      ? getInstructorDisplayName(fromApi)
      : cached?.name || `#${substituteId}`;
   const sectorSlug = normalizeSectorSlug(
      fromApi?.sector ?? fromApi?.sectorSlug ?? cached?.sectorSlug ?? "",
   );
   const profileColor = String(fromApi?.color ?? fromApi?.profileColor ?? "").trim();

   return {
      substituteId,
      name,
      plate: cached?.plate || "",
      ...(cached?.headerColor
         ? { headerColor: cached.headerColor }
         : resolveInstructorHeaderStyles({
              profileColor,
              sectorSlug,
           })),
   };
}

export function buildSubstitutionOverlayByDate(rows, substituteInstructorById) {
   const byDate = new Map();
   const list = Array.isArray(rows) ? rows : EMPTY_LIST;

   for (const row of list) {
      if (row?.isActive === false || row?.is_active === false) continue;
      const dateKey = dateInputValueFromAny(row?.date);
      const baseId = getSubstitutionRowBaseId(row);
      const overlay = resolveSubstitutionOverlayEntry(row, substituteInstructorById);
      if (!dateKey || !baseId || !overlay) continue;

      let dayMap = byDate.get(dateKey);
      if (!dayMap) {
         dayMap = new Map();
         byDate.set(dateKey, dayMap);
      }
      dayMap.set(baseId, overlay);
      const numericBaseId = Number(baseId);
      if (Number.isFinite(numericBaseId) && numericBaseId > 0) {
         dayMap.set(String(numericBaseId), overlay);
      }
   }

   return byDate;
}
